"use client";

import { useState } from "react";
import { Button, Label, Textarea } from "@wtc/ui";
import { Sparkles, Loader2 } from "lucide-react";
import { useMspDocAi, type MspDocAiDraft } from "../../hooks/useMspDocAi";
import type {
  MspDocumentCategory,
  MspDocumentDifficulty,
} from "../msp-document-schema";

interface AiDraftPanelProps {
  title?: string;
  category?: MspDocumentCategory;
  difficulty?: MspDocumentDifficulty;
  hasContent: boolean;
  onDraft: (draft: MspDocAiDraft) => void;
}

/**
 * Prompt-to-draft entry point. The author describes what the doc should cover;
 * the model returns a full markdown draft (+ suggested excerpt/tags) that the
 * author then edits. Nothing is saved automatically.
 */
export function AiDraftPanel({
  title,
  category,
  difficulty,
  hasContent,
  onDraft,
}: AiDraftPanelProps) {
  const [prompt, setPrompt] = useState("");
  const { generateDraft, isGenerating, error } = useMspDocAi();

  const handleGenerate = async () => {
    const draft = await generateDraft({ title, category, difficulty, instructions: prompt });
    if (draft) onDraft(draft);
  };

  const disabled = isGenerating || (!prompt.trim() && !title?.trim());

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
      <div className="flex items-center gap-1.5">
        <Sparkles className="h-4 w-4 text-primary" />
        <Label className="text-sm font-medium text-foreground">Draft with AI</Label>
      </div>
      <p className="text-xs text-muted-foreground">
        Describe what this document should cover. The draft appears in the editor below for you to
        review and edit before saving.
      </p>
      <Textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={3}
        placeholder="e.g. Step-by-step guide to onboarding a new Microsoft 365 tenant for a client, including DNS and security baseline."
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex items-center justify-between gap-2">
        {hasContent ? (
          <span className="text-xs text-muted-foreground">
            Generating replaces the current content.
          </span>
        ) : (
          <span />
        )}
        <Button type="button" size="sm" onClick={handleGenerate} disabled={disabled}>
          {isGenerating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          <span className="ml-1.5">{hasContent ? "Regenerate draft" : "Generate draft"}</span>
        </Button>
      </div>
    </div>
  );
}
